import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import '@/App.css';

// Components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';

// Icons
import { 
  Truck, Package, Users, Settings, LogOut, Sun, Moon, Menu, X, Plus, 
  MapPin, Phone, User, Calendar, Weight, FileText, Bell, Check, Clock,
  ChevronRight, ChevronLeft, ChevronDown, LayoutDashboard, UserCog, Send, ArrowRight, Eye, Pencil, Trash2,
  Copy, ArrowUpDown, Settings2, CheckSquare, Filter
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============ CONTEXT ============

const AuthContext = createContext(null);
const ThemeContext = createContext(null);

export const useAuth = () => useContext(AuthContext);
export const useTheme = () => useContext(ThemeContext);

// ============ API SERVICE ============

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ============ THEME PROVIDER ============

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved || 'light';
  });

  useEffect(() => {
    localStorage.setItem('theme', theme);
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === 'light' ? 'dark' : 'light');

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// ============ AUTH PROVIDER ============

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const res = await api.get('/auth/me');
          setUser(res.data);
          localStorage.setItem('user', JSON.stringify(res.data));
        } catch {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('token', res.data.access_token);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  };

  const register = async (data) => {
    const res = await api.post('/auth/register', data);
    localStorage.setItem('token', res.data.access_token);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// ============ PROTECTED ROUTE ============

const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div></div>;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/deliveries" />;
  }
  
  return children;
};

// ============ LAYOUT ============

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);

  // Save sidebar collapsed state
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', sidebarCollapsed);
  }, [sidebarCollapsed]);

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await api.get('/notifications');
        setNotifications(res.data);
        const unread = res.data.filter(n => !n.read).length;
        setUnreadCount(unread);
      } catch (err) {
        console.error('Error fetching notifications');
      }
    };
    fetchNotifications();
    // Refresh every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const menuItems = [
    { icon: LayoutDashboard, label: 'Панель', path: '/dashboard', roles: ['admin'] },
    { icon: Package, label: 'Заявки', path: '/deliveries', roles: ['admin', 'manager', 'driver', 'customer'] },
    { icon: Plus, label: 'Новая заявка', path: '/deliveries/new', roles: ['customer', 'admin', 'manager'] },
    { icon: Users, label: 'Пользователи', path: '/users', roles: ['admin'] },
    { icon: UserCog, label: 'Водители', path: '/drivers', roles: ['admin', 'manager'] },
    { icon: Settings, label: 'Настройки', path: '/settings', roles: ['admin', 'manager'] },
  ].filter(item => item.roles.includes(user?.role));

  const roleLabels = {
    admin: 'Администратор',
    manager: 'Руководитель',
    driver: 'Водитель',
    customer: 'Заказчик'
  };

  const sidebarWidth = sidebarCollapsed ? 'w-16' : 'w-64';
  const marginLeft = sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64';

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 ${sidebarWidth} bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 transform transition-all duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`${sidebarCollapsed ? 'p-2' : 'p-4 lg:p-6'} border-b border-slate-200 dark:border-slate-700`}>
            <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'}`}>
              <div className={`${sidebarCollapsed ? 'w-10 h-10' : 'w-10 h-10'} rounded-xl bg-gradient-to-br from-sky-500 to-teal-400 flex items-center justify-center flex-shrink-0`}>
                <Truck className="w-5 h-5 text-white" />
              </div>
              {!sidebarCollapsed && (
                <div className="min-w-0">
                  <h1 className="font-bold text-base lg:text-lg text-slate-900 dark:text-white truncate">DeliveryHub</h1>
                  <p className="text-xs text-slate-500 dark:text-slate-400 hidden sm:block">Управление доставками</p>
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 p-2 lg:p-4">
            <nav className="space-y-1">
              {menuItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                    data-testid={`nav-${item.path.replace('/', '')}`}
                    title={sidebarCollapsed ? item.label : undefined}
                    className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'gap-3 px-4'} py-3 rounded-xl text-sm font-medium transition-colors ${isActive ? 'bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50'}`}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
                  </button>
                );
              })}
            </nav>
          </ScrollArea>

          {/* Collapse button - desktop only */}
          <div className="hidden lg:block p-2 border-t border-slate-200 dark:border-slate-700">
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="w-full flex items-center justify-center p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              title={sidebarCollapsed ? 'Развернуть меню' : 'Свернуть меню'}
            >
              {sidebarCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
            </button>
          </div>

          {/* User section */}
          <div className={`${sidebarCollapsed ? 'p-2' : 'p-4'} border-t border-slate-200 dark:border-slate-700`}>
            {!sidebarCollapsed ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{user?.name}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{roleLabels[user?.role]}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2"
                  onClick={logout}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  Выход
                </Button>
              </>
            ) : (
              <button
                onClick={logout}
                className="w-full flex items-center justify-center p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                title="Выход"
              >
                <LogOut className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={`flex-1 ${marginLeft} transition-all duration-300`}>
        {/* Header */}
        <header className="sticky top-0 z-40 bg-white/80 dark:bg-slate-800/80 backdrop-blur-lg border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between px-3 sm:px-4 lg:px-8 h-14 sm:h-16">
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-btn"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            <div className="flex-1" />

            <div className="flex items-center gap-1 sm:gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle"
                className="rounded-full h-9 w-9 sm:h-10 sm:w-10"
              >
                {theme === 'light' ? <Moon className="w-4 h-4 sm:w-5 sm:h-5" /> : <Sun className="w-4 h-4 sm:w-5 sm:h-5" />}
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full relative h-9 w-9 sm:h-10 sm:w-10" data-testid="notifications-btn">
                    <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72 sm:w-80">
                  <div className="p-3 sm:p-4">
                    <p className="font-medium text-sm sm:text-base">Уведомления</p>
                    {notifications.length === 0 ? (
                      <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-2">Нет уведомлений</p>
                    ) : (
                      <div className="mt-2 space-y-2 max-h-60 overflow-y-auto">
                        {notifications.slice(0, 5).map((n) => (
                          <div key={n.id} className="text-xs sm:text-sm p-2 rounded bg-slate-50 dark:bg-slate-700">
                            {n.message}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-3 sm:p-4 lg:p-8">
          {children}
        </main>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

// ============ SSO CALLBACK PAGE ============

const SSOCallback = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const error = params.get('error');

    if (error) {
      toast.error(`Ошибка SSO: ${error}`);
      navigate('/login');
      return;
    }

    if (token) {
      // Save token and fetch user
      localStorage.setItem('token', token);
      api.get('/auth/me')
        .then(res => {
          localStorage.setItem('user', JSON.stringify(res.data));
          toast.success('Вход через SSO успешен!');
          window.location.href = '/dashboard';
        })
        .catch(() => {
          localStorage.removeItem('token');
          toast.error('Ошибка получения данных пользователя');
          navigate('/login');
        });
    } else {
      navigate('/login');
    }
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500 mx-auto mb-4"></div>
        <p className="text-slate-600 dark:text-slate-300">Авторизация через SSO...</p>
      </div>
    </div>
  );
};

// ============ LOGIN PAGE ============

const LoginPage = () => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'customer'
  });

  // Check for SSO error in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    if (error) {
      const errorMessages = {
        'sso_denied': 'Доступ отклонен',
        'missing_params': 'Отсутствуют параметры',
        'invalid_state': 'Неверное состояние сессии',
        'token_exchange_failed': 'Ошибка обмена токена',
        'userinfo_failed': 'Ошибка получения данных',
        'no_email': 'Email не получен от SSO',
        'sso_error': 'Ошибка SSO'
      };
      toast.error(errorMessages[error] || `Ошибка SSO: ${error}`);
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        await register(formData);
        toast.success('Регистрация успешна!');
      } else {
        await login(formData.email, formData.password);
        toast.success('Добро пожаловать!');
      }
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка авторизации');
    } finally {
      setLoading(false);
    }
  };

  const handleSSOLogin = async () => {
    setSsoLoading(true);
    try {
      const res = await api.get('/auth/sso/login');
      // Redirect to SSO authorization
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка SSO');
      setSsoLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-500 to-teal-400 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-sky-500/30">
            <Truck className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">DeliveryHub</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Управление доставками и забором грузов</p>
        </div>

        <Card className="border-0 shadow-xl shadow-slate-200/50 dark:shadow-none dark:bg-slate-800">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl">{isRegister ? 'Регистрация' : 'Вход'}</CardTitle>
            <CardDescription>
              {isRegister ? 'Создайте аккаунт для доступа к системе' : 'Войдите в свой аккаунт'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* SSO Login Button */}
            {!isRegister && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full mb-4 h-11 border-2 hover:bg-slate-50 dark:hover:bg-slate-700"
                  onClick={handleSSOLogin}
                  disabled={ssoLoading}
                  data-testid="sso-login-btn"
                >
                  {ssoLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-sky-500 mr-2"></div>
                  ) : (
                    <Users className="w-4 h-4 mr-2" />
                  )}
                  Войти через IRISK ID
                </Button>
                
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <Separator />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white dark:bg-slate-800 px-2 text-slate-500">или</span>
                  </div>
                </div>
              </>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {isRegister && (
                <div className="space-y-2">
                  <Label htmlFor="name">Имя</Label>
                  <Input
                    id="name"
                    data-testid="register-name"
                    placeholder="Ваше имя"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="login-email"
                  placeholder="email@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Пароль</Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="login-password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
              </div>

              {isRegister && (
                <div className="space-y-2">
                  <Label>Роль</Label>
                  <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                    <SelectTrigger data-testid="register-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="customer">Заказчик</SelectItem>
                      <SelectItem value="driver">Водитель</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button
                type="submit"
                className="w-full btn-primary"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? 'Загрузка...' : (isRegister ? 'Зарегистрироваться' : 'Войти')}
              </Button>
            </form>

            <div className="mt-6">
              <Separator className="my-4" />
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => setIsRegister(!isRegister)}
                data-testid="toggle-auth-mode"
              >
                {isRegister ? 'Уже есть аккаунт? Войти' : 'Нет аккаунта? Регистрация'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ============ DASHBOARD ============

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentDeliveries, setRecentDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [deliveriesRes] = await Promise.all([
          api.get('/deliveries')
        ]);
        setRecentDeliveries(deliveriesRes.data.slice(0, 5));
        
        if (['admin', 'manager'].includes(user.role)) {
          const statsRes = await api.get('/stats');
          setStats(statsRes.data);
        }
      } catch (err) {
        toast.error('Ошибка загрузки данных');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user.role]);

  const statusColors = {
    new: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    in_transit: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
    delivered: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
  };

  const statusLabels = {
    new: 'Новая',
    in_transit: 'В пути',
    delivered: 'Доставлено'
  };

  const roleLabels = {
    admin: 'Администратор',
    manager: 'Руководитель',
    driver: 'Водитель',
    customer: 'Заказчик'
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div></div>;
  }

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Привет, {user.name}!</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">{roleLabels[user.role]} • Сегодня {new Date().toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
      </div>

      {/* Stats for admin/manager */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-0 shadow-lg shadow-slate-200/50 dark:shadow-none dark:bg-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Всего заявок</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">{stats.deliveries.total}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center">
                  <Package className="w-6 h-6 text-sky-600 dark:text-sky-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg shadow-slate-200/50 dark:shadow-none dark:bg-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Новых</p>
                  <p className="text-3xl font-bold text-amber-600 dark:text-amber-400 mt-1">{stats.deliveries.new}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg shadow-slate-200/50 dark:shadow-none dark:bg-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">В пути</p>
                  <p className="text-3xl font-bold text-sky-600 dark:text-sky-400 mt-1">{stats.deliveries.in_transit}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center">
                  <Truck className="w-6 h-6 text-sky-600 dark:text-sky-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg shadow-slate-200/50 dark:shadow-none dark:bg-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Доставлено</p>
                  <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">{stats.deliveries.delivered}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <Check className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {user.role === 'customer' && (
          <Card className="border-0 shadow-lg shadow-slate-200/50 dark:shadow-none dark:bg-slate-800 hover:shadow-xl transition-shadow cursor-pointer" onClick={() => navigate('/deliveries/new')}>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-teal-400 flex items-center justify-center shadow-lg shadow-sky-500/30">
                <Plus className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 dark:text-white">Создать заявку</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">Оформить доставку или забор груза</p>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </CardContent>
          </Card>
        )}

        <Card className="border-0 shadow-lg shadow-slate-200/50 dark:shadow-none dark:bg-slate-800 hover:shadow-xl transition-shadow cursor-pointer" onClick={() => navigate('/deliveries')}>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
              <Package className="w-6 h-6 text-slate-600 dark:text-slate-300" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 dark:text-white">Все заявки</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">Просмотр и управление заявками</p>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </CardContent>
        </Card>
      </div>

      {/* Recent deliveries */}
      <Card className="border-0 shadow-lg shadow-slate-200/50 dark:shadow-none dark:bg-slate-800">
        <CardHeader>
          <CardTitle className="text-lg">Последние заявки</CardTitle>
        </CardHeader>
        <CardContent>
          {recentDeliveries.length === 0 ? (
            <p className="text-center text-slate-500 dark:text-slate-400 py-8">Нет заявок</p>
          ) : (
            <div className="space-y-3">
              {recentDeliveries.map((delivery) => (
                <div
                  key={delivery.id}
                  className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                  onClick={() => navigate(`/deliveries/${delivery.id}`)}
                >
                  <div className="w-10 h-10 rounded-lg bg-white dark:bg-slate-600 shadow-sm flex items-center justify-center">
                    {delivery.delivery_type === 'pickup' ? (
                      <Package className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                    ) : (
                      <Truck className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 dark:text-white truncate">#{delivery.id.slice(0, 8)}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{delivery.address_to}</p>
                  </div>
                  <Badge className={statusColors[delivery.status]}>
                    {statusLabels[delivery.status]}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============ DELIVERIES LIST ============

// Default visible columns
const DEFAULT_COLUMNS = ['number', 'type', 'author', 'address', 'status', 'driver', 'deadline', 'created', 'actions'];

const ALL_COLUMNS = [
  { id: 'number', label: '№', alwaysVisible: true },
  { id: 'type', label: 'Тип' },
  { id: 'author', label: 'Автор' },
  { id: 'sender', label: 'Отправитель' },
  { id: 'recipient', label: 'Получатель' },
  { id: 'address', label: 'Адрес' },
  { id: 'tk', label: 'ТК' },
  { id: 'places', label: 'Мест' },
  { id: 'status', label: 'Статус' },
  { id: 'driver', label: 'Водитель' },
  { id: 'deadline', label: 'Дедлайн' },
  { id: 'created', label: 'Создана' },
  { id: 'notes', label: 'Примечания' },
  { id: 'handler', label: 'Кому передать' },
  { id: 'safe_package', label: 'Сейф-пакет', roles: ['manager'] },
  { id: 'receipt_number', label: 'Приходный лист', roles: ['manager'] },
  { id: 'actions', label: '', alwaysVisible: true },
];

const DeliveriesList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [deadlineFrom, setDeadlineFrom] = useState('');
  const [deadlineTo, setDeadlineTo] = useState('');
  const [drivers, setDrivers] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [selectedDeliveries, setSelectedDeliveries] = useState([]);
  const [massAssignDialogOpen, setMassAssignDialogOpen] = useState(false);
  const [massStatusDialogOpen, setMassStatusDialogOpen] = useState(false);
  const [massStatus, setMassStatus] = useState('');
  const [sortByDeadline, setSortByDeadline] = useState(null); // null, 'asc', 'desc'
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deliveryToDelete, setDeliveryToDelete] = useState(null);
  const [massDeleteDialogOpen, setMassDeleteDialogOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('deliveryColumns');
    return saved ? JSON.parse(saved) : DEFAULT_COLUMNS;
  });

  // Save column preferences
  useEffect(() => {
    localStorage.setItem('deliveryColumns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  const fetchDeliveries = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (deadlineFrom) params.deadline_from = deadlineFrom;
      if (deadlineTo) params.deadline_to = deadlineTo;
      
      const res = await api.get('/deliveries', { params });
      setDeliveries(res.data);
    } catch (err) {
      toast.error('Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, dateFrom, dateTo, deadlineFrom, deadlineTo]);

  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  useEffect(() => {
    if (['admin', 'manager'].includes(user.role)) {
      api.get('/drivers').then(res => setDrivers(res.data)).catch(() => {});
    }
  }, [user.role]);

  const handleAssign = async () => {
    if (!selectedDelivery || !selectedDriver) return;
    try {
      await api.post(`/deliveries/${selectedDelivery}/assign?driver_id=${selectedDriver}`);
      toast.success('Водитель назначен, статус изменён на "В пути"');
      setAssignDialogOpen(false);
      setSelectedDelivery(null);
      setSelectedDriver('');
      fetchDeliveries();
    } catch (err) {
      toast.error('Ошибка назначения');
    }
  };

  const handleMassAssign = async () => {
    if (selectedDeliveries.length === 0 || !selectedDriver) return;
    try {
      await api.post('/deliveries/mass-assign', {
        delivery_ids: selectedDeliveries,
        driver_id: selectedDriver
      });
      toast.success(`Водитель назначен на ${selectedDeliveries.length} заявок, статус изменён на "В пути"`);
      setMassAssignDialogOpen(false);
      setSelectedDeliveries([]);
      setSelectedDriver('');
      fetchDeliveries();
    } catch (err) {
      toast.error('Ошибка назначения');
    }
  };

  const handleMassStatusChange = async () => {
    if (selectedDeliveries.length === 0 || !massStatus) return;
    try {
      await api.post('/deliveries/mass-status', {
        delivery_ids: selectedDeliveries,
        status: massStatus
      });
      toast.success(`Статус изменён для ${selectedDeliveries.length} заявок`);
      setMassStatusDialogOpen(false);
      setSelectedDeliveries([]);
      setMassStatus('');
      fetchDeliveries();
    } catch (err) {
      toast.error('Ошибка изменения статуса');
    }
  };

  const handleStatusChange = async (deliveryId, newStatus) => {
    try {
      await api.put(`/deliveries/${deliveryId}`, { status: newStatus });
      toast.success('Статус обновлен');
      fetchDeliveries();
    } catch (err) {
      toast.error('Ошибка обновления');
    }
  };

  const handleCopyDelivery = async (e, deliveryId) => {
    e.stopPropagation();
    try {
      const res = await api.post(`/deliveries/${deliveryId}/copy`);
      toast.success(`Заявка скопирована: ${res.data.display_number}`);
      fetchDeliveries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка копирования');
    }
  };

  const handleDeleteDelivery = async () => {
    if (!deliveryToDelete) return;
    try {
      await api.delete(`/deliveries/${deliveryToDelete}`);
      toast.success('Заявка удалена');
      setDeleteDialogOpen(false);
      setDeliveryToDelete(null);
      fetchDeliveries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const handleMassDelete = async () => {
    if (selectedDeliveries.length === 0) return;
    try {
      // Delete selected deliveries one by one
      await Promise.all(selectedDeliveries.map(id => api.delete(`/deliveries/${id}`)));
      toast.success(`Удалено заявок: ${selectedDeliveries.length}`);
      setMassDeleteDialogOpen(false);
      setSelectedDeliveries([]);
      fetchDeliveries();
    } catch (err) {
      toast.error('Ошибка удаления');
    }
  };

  const toggleSelectDelivery = (deliveryId) => {
    setSelectedDeliveries(prev => 
      prev.includes(deliveryId) 
        ? prev.filter(id => id !== deliveryId)
        : [...prev, deliveryId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedDeliveries.length === deliveries.length) {
      setSelectedDeliveries([]);
    } else {
      setSelectedDeliveries(deliveries.map(d => d.id));
    }
  };

  const toggleColumn = (columnId) => {
    setVisibleColumns(prev => 
      prev.includes(columnId) 
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };

  const toggleDeadlineSort = () => {
    if (sortByDeadline === null) {
      setSortByDeadline('asc');
    } else if (sortByDeadline === 'asc') {
      setSortByDeadline('desc');
    } else {
      setSortByDeadline(null);
    }
  };

  // Sort deliveries by deadline if sorting is active
  const sortedDeliveries = [...deliveries].sort((a, b) => {
    if (sortByDeadline === null) return 0;
    const dateA = a.deadline ? new Date(a.deadline) : new Date('9999-12-31');
    const dateB = b.deadline ? new Date(b.deadline) : new Date('9999-12-31');
    return sortByDeadline === 'asc' ? dateA - dateB : dateB - dateA;
  });

  const statusColors = {
    new: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    in_transit: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
    delivered: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
  };

  const statusLabels = {
    new: 'Новая',
    in_transit: 'В пути',
    delivered: 'Доставлено'
  };

  const typeLabels = {
    delivery: 'Доставка',
    pickup: 'Забор'
  };

  const canChangeStatus = ['admin', 'manager', 'driver'].includes(user.role);
  const canAssign = ['admin', 'manager'].includes(user.role);

  return (
    <div className="space-y-6" data-testid="deliveries-list">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Заявки</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Управление заявками на доставку</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canAssign && selectedDeliveries.length > 0 && (
            <>
              <Button onClick={() => setMassAssignDialogOpen(true)} className="btn-primary" data-testid="mass-assign-btn">
                Назначить ({selectedDeliveries.length})
              </Button>
              <Button onClick={() => setMassStatusDialogOpen(true)} variant="outline" data-testid="mass-status-btn">
                <CheckSquare className="w-4 h-4 mr-2" />
                Статус ({selectedDeliveries.length})
              </Button>
              <Button onClick={() => setMassDeleteDialogOpen(true)} variant="outline" className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20" data-testid="mass-delete-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Удалить ({selectedDeliveries.length})
              </Button>
            </>
          )}
          <Button variant="outline" size="icon" onClick={() => setColumnsDialogOpen(true)} title="Настройка колонок" data-testid="columns-btn">
            <Settings2 className="w-4 h-4" />
          </Button>
          {['customer', 'admin', 'manager'].includes(user.role) && (
            <Button onClick={() => navigate('/deliveries/new')} className="btn-primary" data-testid="new-delivery-btn">
              <Plus className="w-4 h-4 mr-2" />
              Новая
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-lg shadow-slate-200/50 dark:shadow-none dark:bg-slate-800">
        <CardContent className="p-3 sm:p-4">
          {/* Mobile filter toggle */}
          <button 
            className="md:hidden w-full flex items-center justify-between p-2 mb-2 rounded-lg bg-slate-50 dark:bg-slate-700/50"
            onClick={() => setFiltersExpanded(!filtersExpanded)}
          >
            <span className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-300">
              <Filter className="w-4 h-4" />
              Фильтры
              {(statusFilter !== 'all' || dateFrom || dateTo || deadlineFrom || deadlineTo) && (
                <Badge className="bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400">активны</Badge>
              )}
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${filtersExpanded ? 'rotate-180' : ''}`} />
          </button>
          
          <div className={`${filtersExpanded ? 'block' : 'hidden'} md:block`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
              <div>
                <Label className="text-xs mb-1 block">Статус</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="status-filter" className="h-9">
                    <SelectValue placeholder="Все" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все</SelectItem>
                    <SelectItem value="new">Новые</SelectItem>
                    <SelectItem value="in_transit">В пути</SelectItem>
                    <SelectItem value="delivered">Доставлено</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Дата создания от</Label>
                <Input 
                  type="date" 
                  value={dateFrom} 
                  onChange={(e) => setDateFrom(e.target.value)}
                  data-testid="date-from"
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Дата создания до</Label>
                <Input 
                  type="date" 
                  value={dateTo} 
                  onChange={(e) => setDateTo(e.target.value)}
                  data-testid="date-to"
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Дедлайн от</Label>
                <Input 
                  type="date" 
                  value={deadlineFrom} 
                  onChange={(e) => setDeadlineFrom(e.target.value)}
                  data-testid="deadline-from"
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Дедлайн до</Label>
                <Input 
                  type="date" 
                  value={deadlineTo} 
                  onChange={(e) => setDeadlineTo(e.target.value)}
                  data-testid="deadline-to"
                  className="h-9"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div></div>
      ) : sortedDeliveries.length === 0 ? (
        <Card className="border-0 shadow-lg dark:bg-slate-800">
          <CardContent className="py-12 text-center">
            <Package className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <p className="text-slate-500 dark:text-slate-400">Заявок не найдено</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-0 shadow-lg shadow-slate-200/50 dark:shadow-none dark:bg-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
          <Table className="min-w-[700px]">
            <TableHeader>
              <TableRow>
                {canAssign && (
                  <TableHead className="w-10">
                    <input 
                      type="checkbox" 
                      checked={selectedDeliveries.length === sortedDeliveries.length && sortedDeliveries.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded"
                    />
                  </TableHead>
                )}
                {visibleColumns.includes('number') && <TableHead>№</TableHead>}
                {visibleColumns.includes('type') && <TableHead>Тип</TableHead>}
                {visibleColumns.includes('author') && <TableHead>Автор</TableHead>}
                {visibleColumns.includes('sender') && <TableHead>Отправитель</TableHead>}
                {visibleColumns.includes('recipient') && <TableHead>Получатель</TableHead>}
                {visibleColumns.includes('address') && <TableHead>Адрес</TableHead>}
                {visibleColumns.includes('tk') && <TableHead>ТК</TableHead>}
                {visibleColumns.includes('places') && <TableHead>Мест</TableHead>}
                {visibleColumns.includes('status') && <TableHead>Статус</TableHead>}
                {visibleColumns.includes('driver') && <TableHead>Водитель</TableHead>}
                {visibleColumns.includes('deadline') && (
                  <TableHead>
                    <button 
                      onClick={toggleDeadlineSort}
                      className="flex items-center gap-1 hover:text-sky-500"
                      data-testid="sort-deadline"
                    >
                      Дедлайн
                      <ArrowUpDown className={`w-4 h-4 ${sortByDeadline ? 'text-sky-500' : ''}`} />
                      {sortByDeadline && <span className="text-xs">({sortByDeadline === 'asc' ? '↑' : '↓'})</span>}
                    </button>
                  </TableHead>
                )}
                {visibleColumns.includes('created') && <TableHead>Создана</TableHead>}
                {visibleColumns.includes('notes') && <TableHead>Примечания</TableHead>}
                {visibleColumns.includes('handler') && <TableHead>Кому передать</TableHead>}
                {visibleColumns.includes('safe_package') && user.role === 'manager' && <TableHead>Сейф-пакет</TableHead>}
                {visibleColumns.includes('receipt_number') && user.role === 'manager' && <TableHead>Приходный лист</TableHead>}
                {visibleColumns.includes('actions') && <TableHead className="w-10"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedDeliveries.map((delivery) => (
                <TableRow 
                  key={delivery.id} 
                  className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50"
                  onClick={() => navigate(`/deliveries/${delivery.id}`)}
                >
                  {canAssign && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <input 
                        type="checkbox" 
                        checked={selectedDeliveries.includes(delivery.id)}
                        onChange={() => toggleSelectDelivery(delivery.id)}
                        className="rounded"
                      />
                    </TableCell>
                  )}
                  {visibleColumns.includes('number') && (
                    <TableCell className="font-mono text-sm font-medium text-slate-900 dark:text-white">
                      {delivery.display_number || `#${delivery.id.slice(0, 8)}`}
                    </TableCell>
                  )}
                  {visibleColumns.includes('type') && (
                    <TableCell>{typeLabels[delivery.delivery_type]}</TableCell>
                  )}
                  {visibleColumns.includes('author') && (
                    <TableCell className="font-medium">{delivery.customer_name || '-'}</TableCell>
                  )}
                  {visibleColumns.includes('sender') && (
                    <TableCell>{delivery.sender || '-'}</TableCell>
                  )}
                  {visibleColumns.includes('recipient') && (
                    <TableCell>{delivery.recipient_name || '-'}</TableCell>
                  )}
                  {visibleColumns.includes('address') && (
                    <TableCell className="max-w-[150px] truncate">
                      {delivery.delivery_type === 'pickup' ? delivery.address_from : delivery.address_to}
                    </TableCell>
                  )}
                  {visibleColumns.includes('tk') && (
                    <TableCell>{delivery.tk_name || '-'}</TableCell>
                  )}
                  {visibleColumns.includes('places') && (
                    <TableCell>{delivery.places_count || '-'}</TableCell>
                  )}
                  {visibleColumns.includes('status') && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {canChangeStatus && delivery.status !== 'delivered' ? (
                        <Select value={delivery.status} onValueChange={(v) => handleStatusChange(delivery.id, v)}>
                          <SelectTrigger className="h-8 w-32">
                            <Badge className={statusColors[delivery.status]}>{statusLabels[delivery.status]}</Badge>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">Новая</SelectItem>
                            <SelectItem value="in_transit">В пути</SelectItem>
                            <SelectItem value="delivered">Доставлено</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge className={statusColors[delivery.status]}>{statusLabels[delivery.status]}</Badge>
                      )}
                    </TableCell>
                  )}
                  {visibleColumns.includes('driver') && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {canAssign ? (
                        <Button
                          size="sm"
                          variant={delivery.driver_name ? "ghost" : "outline"}
                          onClick={() => { setSelectedDelivery(delivery.id); setAssignDialogOpen(true); }}
                          data-testid={`assign-driver-${delivery.id}`}
                        >
                          {delivery.driver_name || 'Назначить'}
                        </Button>
                      ) : (
                        <span className="text-sm">{delivery.driver_name || '-'}</span>
                      )}
                    </TableCell>
                  )}
                  {visibleColumns.includes('deadline') && (
                    <TableCell className="text-sm text-slate-500">
                      {delivery.deadline || '-'}
                    </TableCell>
                  )}
                  {visibleColumns.includes('created') && (
                    <TableCell className="text-sm text-slate-500">
                      {new Date(delivery.created_at).toLocaleDateString('ru-RU')}
                    </TableCell>
                  )}
                  {visibleColumns.includes('notes') && (
                    <TableCell className="max-w-[150px] truncate text-sm text-slate-500">
                      {delivery.notes || '-'}
                    </TableCell>
                  )}
                  {visibleColumns.includes('handler') && (
                    <TableCell className="text-sm">
                      {delivery.handler_name || '-'}
                    </TableCell>
                  )}
                  {visibleColumns.includes('safe_package') && user.role === 'manager' && (
                    <TableCell className="text-sm">
                      {delivery.is_safe_package ? (
                        <span className="text-emerald-600 dark:text-emerald-400">{delivery.safe_package_number || '✓'}</span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                  )}
                  {visibleColumns.includes('receipt_number') && user.role === 'manager' && (
                    <TableCell className="text-sm font-mono">
                      {delivery.receipt_number || '-'}
                    </TableCell>
                  )}
                  {visibleColumns.includes('actions') && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={(e) => handleCopyDelivery(e, delivery.id)}
                          title="Копировать заявку"
                          data-testid={`copy-delivery-${delivery.id}`}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        {canAssign && (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); setDeliveryToDelete(delivery.id); setDeleteDialogOpen(true); }}
                            title="Удалить заявку"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                            data-testid={`delete-delivery-${delivery.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </Card>
      )}

      {/* Assign driver dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Назначить водителя</DialogTitle>
            <DialogDescription>Выберите водителя для этой заявки</DialogDescription>
          </DialogHeader>
          <Select value={selectedDriver} onValueChange={setSelectedDriver}>
            <SelectTrigger data-testid="select-driver">
              <SelectValue placeholder="Выберите водителя" />
            </SelectTrigger>
            <SelectContent>
              {drivers.map((driver) => (
                <SelectItem key={driver.id} value={driver.id}>{driver.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Отмена</Button>
            <Button onClick={handleAssign} disabled={!selectedDriver} className="btn-primary" data-testid="confirm-assign">Назначить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mass assign dialog */}
      <Dialog open={massAssignDialogOpen} onOpenChange={setMassAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Массовое назначение</DialogTitle>
            <DialogDescription>Назначить водителя на {selectedDeliveries.length} заявок</DialogDescription>
          </DialogHeader>
          <Select value={selectedDriver} onValueChange={setSelectedDriver}>
            <SelectTrigger data-testid="select-driver-mass">
              <SelectValue placeholder="Выберите водителя" />
            </SelectTrigger>
            <SelectContent>
              {drivers.map((driver) => (
                <SelectItem key={driver.id} value={driver.id}>{driver.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMassAssignDialogOpen(false)}>Отмена</Button>
            <Button onClick={handleMassAssign} disabled={!selectedDriver} className="btn-primary" data-testid="confirm-mass-assign">
              Назначить на {selectedDeliveries.length} заявок
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mass status change dialog */}
      <Dialog open={massStatusDialogOpen} onOpenChange={setMassStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Массовое изменение статуса</DialogTitle>
            <DialogDescription>Изменить статус для {selectedDeliveries.length} заявок</DialogDescription>
          </DialogHeader>
          <Select value={massStatus} onValueChange={setMassStatus}>
            <SelectTrigger data-testid="select-mass-status">
              <SelectValue placeholder="Выберите новый статус" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new">Новая</SelectItem>
              <SelectItem value="in_transit">В пути</SelectItem>
              <SelectItem value="delivered">Доставлено</SelectItem>
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMassStatusDialogOpen(false); setMassStatus(''); }}>Отмена</Button>
            <Button onClick={handleMassStatusChange} disabled={!massStatus} className="btn-primary" data-testid="confirm-mass-status">
              Изменить статус
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Column settings dialog */}
      <Dialog open={columnsDialogOpen} onOpenChange={setColumnsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Настройка колонок</DialogTitle>
            <DialogDescription>Выберите колонки для отображения в таблице</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-4">
            {ALL_COLUMNS.filter(col => !col.roles || col.roles.includes(user.role)).map((col) => (
              <div key={col.id} className="flex items-center space-x-2">
                <Checkbox
                  id={`col-${col.id}`}
                  checked={visibleColumns.includes(col.id)}
                  onCheckedChange={() => !col.alwaysVisible && toggleColumn(col.id)}
                  disabled={col.alwaysVisible}
                />
                <label
                  htmlFor={`col-${col.id}`}
                  className={`text-sm cursor-pointer ${col.alwaysVisible ? 'text-slate-400' : ''}`}
                >
                  {col.label || 'Действия'}
                </label>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVisibleColumns(DEFAULT_COLUMNS)}>По умолчанию</Button>
            <Button onClick={() => setColumnsDialogOpen(false)} className="btn-primary">Готово</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete single delivery dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Удалить заявку?</DialogTitle>
            <DialogDescription>
              Это действие нельзя отменить. Заявка будет удалена безвозвратно.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setDeliveryToDelete(null); }}>Отмена</Button>
            <Button onClick={handleDeleteDelivery} variant="destructive" data-testid="confirm-delete">
              Удалить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mass delete dialog */}
      <Dialog open={massDeleteDialogOpen} onOpenChange={setMassDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Удалить {selectedDeliveries.length} заявок?</DialogTitle>
            <DialogDescription>
              Это действие нельзя отменить. Все выбранные заявки будут удалены безвозвратно.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMassDeleteDialogOpen(false)}>Отмена</Button>
            <Button onClick={handleMassDelete} variant="destructive" data-testid="confirm-mass-delete">
              Удалить {selectedDeliveries.length} заявок
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ NEW DELIVERY ============

const NewDelivery = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [directories, setDirectories] = useState({
    tk: [],
    recipients: [],
    handlers: []
  });
  const [formData, setFormData] = useState({
    delivery_type: 'delivery',
    sender: '',
    // Delivery fields
    recipient_name: '',
    address_to: '',
    tk_id: '',
    places_count: 1,
    is_safe_package: false,
    safe_package_number: '',
    // Pickup fields
    recipient_id: '',
    address_from: '',
    receipt_number: '',
    handler_id: '',
    // Common
    deadline: '',
    notes: ''
  });

  useEffect(() => {
    const fetchDirectories = async () => {
      try {
        const [tkRes, recipientsRes, handlersRes] = await Promise.all([
          api.get('/directories/tk'),
          api.get('/directories/recipients'),
          api.get('/directories/handlers')
        ]);
        setDirectories({
          tk: tkRes.data,
          recipients: recipientsRes.data,
          handlers: handlersRes.data
        });
      } catch (err) {
        console.error('Error loading directories:', err);
      }
    };
    fetchDirectories();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = { ...formData };
      
      // Add tk_name if tk_id selected
      if (data.tk_id) {
        const tk = directories.tk.find(t => t.id === data.tk_id);
        if (tk) data.tk_name = tk.name;
      }
      
      // Add handler_name if handler_id selected
      if (data.handler_id) {
        const handler = directories.handlers.find(h => h.id === data.handler_id);
        if (handler) data.handler_name = handler.name;
      }
      
      // Add recipient_name from directory for pickup
      if (data.delivery_type === 'pickup' && data.recipient_id) {
        const recipient = directories.recipients.find(r => r.id === data.recipient_id);
        if (recipient) data.recipient_name = recipient.name;
      }
      
      await api.post('/deliveries', data);
      toast.success('Заявка создана!');
      navigate('/deliveries');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка создания');
    } finally {
      setLoading(false);
    }
  };

  const isDelivery = formData.delivery_type === 'delivery';

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="new-delivery-form">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Новая заявка</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Заполните форму для создания заявки</p>
      </div>

      <Card className="border-0 shadow-lg shadow-slate-200/50 dark:shadow-none dark:bg-slate-800">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Тип заявки */}
            <div className="space-y-2">
              <Label>Тип заявки *</Label>
              <Select value={formData.delivery_type} onValueChange={(v) => setFormData({ ...formData, delivery_type: v })}>
                <SelectTrigger data-testid="delivery-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="delivery">Доставка</SelectItem>
                  <SelectItem value="pickup">Забор груза</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Отправитель (общее поле) */}
            <div className="space-y-2">
              <Label>Отправитель *</Label>
              <Input
                placeholder="Имя или название отправителя"
                value={formData.sender}
                onChange={(e) => setFormData({ ...formData, sender: e.target.value })}
                required
                data-testid="sender"
              />
            </div>

            {isDelivery ? (
              <>
                {/* ДОСТАВКА */}
                <div className="space-y-2">
                  <Label>Получатель *</Label>
                  <Input
                    placeholder="Имя или название получателя"
                    value={formData.recipient_name}
                    onChange={(e) => setFormData({ ...formData, recipient_name: e.target.value })}
                    required
                    data-testid="recipient-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Адрес доставки</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <Input
                      className="pl-10"
                      placeholder="Улица, дом, город"
                      value={formData.address_to}
                      onChange={(e) => setFormData({ ...formData, address_to: e.target.value })}
                      data-testid="address-to"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Транспортная компания (ТК)</Label>
                    <Select value={formData.tk_id} onValueChange={(v) => setFormData({ ...formData, tk_id: v })}>
                      <SelectTrigger data-testid="tk-select">
                        <SelectValue placeholder="Выберите ТК" />
                      </SelectTrigger>
                      <SelectContent>
                        {directories.tk.map((tk) => (
                          <SelectItem key={tk.id} value={tk.id}>{tk.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Кол-во мест</Label>
                    <Input
                      type="number"
                      min="1"
                      max="999"
                      value={formData.places_count}
                      onChange={(e) => setFormData({ ...formData, places_count: parseInt(e.target.value) || 1 })}
                      data-testid="places-count"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={formData.is_safe_package}
                      onCheckedChange={(v) => setFormData({ ...formData, is_safe_package: v, safe_package_number: v ? formData.safe_package_number : '' })}
                      data-testid="safe-package-toggle"
                    />
                    <Label>Сейф-пакет</Label>
                  </div>

                  {formData.is_safe_package && (
                    <div className="space-y-2">
                      <Label>Номер сейф-пакета</Label>
                      <Input
                        placeholder="Введите номер"
                        value={formData.safe_package_number}
                        onChange={(e) => setFormData({ ...formData, safe_package_number: e.target.value })}
                        data-testid="safe-package-number"
                      />
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* ЗАБОР ГРУЗА */}
                <div className="space-y-2">
                  <Label>Получатель *</Label>
                  <Select value={formData.recipient_id} onValueChange={(v) => setFormData({ ...formData, recipient_id: v })}>
                    <SelectTrigger data-testid="recipient-select">
                      <SelectValue placeholder="Выберите получателя" />
                    </SelectTrigger>
                    <SelectContent>
                      {directories.recipients.map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Адрес получения *</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <Input
                      className="pl-10"
                      placeholder="Улица, дом, город"
                      value={formData.address_from}
                      onChange={(e) => setFormData({ ...formData, address_from: e.target.value })}
                      required
                      data-testid="address-from"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Номер приходного листа</Label>
                  <Input
                    placeholder="Введите номер"
                    value={formData.receipt_number}
                    onChange={(e) => setFormData({ ...formData, receipt_number: e.target.value })}
                    data-testid="receipt-number"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Кому передать?</Label>
                  <Select value={formData.handler_id} onValueChange={(v) => setFormData({ ...formData, handler_id: v })}>
                    <SelectTrigger data-testid="handler-select">
                      <SelectValue placeholder="Выберите получателя" />
                    </SelectTrigger>
                    <SelectContent>
                      {directories.handlers.map((h) => (
                        <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Комментарий</Label>
                  <Textarea
                    placeholder="Дополнительная информация..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    data-testid="notes"
                  />
                </div>
              </>
            )}

            {/* Дедлайн (общее поле) */}
            <div className="space-y-2">
              <Label>Дедлайн</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                <Input
                  type="date"
                  className="pl-10"
                  value={formData.deadline}
                  onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                  data-testid="deadline"
                />
              </div>
            </div>

            {/* Примечания для доставки */}
            {isDelivery && (
              <div className="space-y-2">
                <Label>Примечания</Label>
                <Textarea
                  placeholder="Дополнительная информация..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  data-testid="notes"
                />
              </div>
            )}

            <div className="flex gap-4">
              <Button type="button" variant="outline" onClick={() => navigate('/deliveries')} className="flex-1">
                Отмена
              </Button>
              <Button type="submit" disabled={loading} className="flex-1 btn-primary" data-testid="submit-delivery">
                {loading ? 'Создание...' : 'Создать заявку'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// ============ EDIT DELIVERY ============

const EditDelivery = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { deliveryId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [delivery, setDelivery] = useState(null);
  const [directories, setDirectories] = useState({
    tk: [],
    recipients: [],
    handlers: []
  });
  const [formData, setFormData] = useState({
    delivery_type: 'delivery',
    sender: '',
    recipient_name: '',
    address_to: '',
    tk_id: '',
    places_count: 1,
    is_safe_package: false,
    safe_package_number: '',
    recipient_id: '',
    address_from: '',
    receipt_number: '',
    handler_id: '',
    deadline: '',
    notes: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [deliveryRes, tkRes, recipientsRes, handlersRes] = await Promise.all([
          api.get(`/deliveries/${deliveryId}`),
          api.get('/directories/tk'),
          api.get('/directories/recipients'),
          api.get('/directories/handlers')
        ]);
        
        const d = deliveryRes.data;
        setDelivery(d);
        setDirectories({
          tk: tkRes.data,
          recipients: recipientsRes.data,
          handlers: handlersRes.data
        });
        
        // Check permissions
        if (user.role === 'customer') {
          if (d.customer_id !== user.id) {
            toast.error('Доступ запрещён');
            navigate('/deliveries');
            return;
          }
          if (d.status !== 'new') {
            toast.error('Редактирование запрещено — заявка уже в работе');
            navigate(`/deliveries/${deliveryId}`);
            return;
          }
        }
        
        // Populate form
        setFormData({
          delivery_type: d.delivery_type || 'delivery',
          sender: d.sender || '',
          recipient_name: d.recipient_name || '',
          address_to: d.address_to || '',
          tk_id: d.tk_id || '',
          places_count: d.places_count || 1,
          is_safe_package: d.is_safe_package || false,
          safe_package_number: d.safe_package_number || '',
          recipient_id: d.recipient_id || '',
          address_from: d.address_from || '',
          receipt_number: d.receipt_number || '',
          handler_id: d.handler_id || '',
          deadline: d.deadline || '',
          notes: d.notes || ''
        });
      } catch (err) {
        toast.error('Ошибка загрузки заявки');
        navigate('/deliveries');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [deliveryId, navigate, user.id, user.role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = { ...formData };
      
      // Add tk_name if tk_id selected
      if (data.tk_id) {
        const tk = directories.tk.find(t => t.id === data.tk_id);
        if (tk) data.tk_name = tk.name;
      }
      
      // Add handler_name if handler_id selected
      if (data.handler_id) {
        const handler = directories.handlers.find(h => h.id === data.handler_id);
        if (handler) data.handler_name = handler.name;
      }
      
      // Add recipient_name from directory for pickup
      if (data.delivery_type === 'pickup' && data.recipient_id) {
        const recipient = directories.recipients.find(r => r.id === data.recipient_id);
        if (recipient) data.recipient_name = recipient.name;
      }
      
      await api.put(`/deliveries/${deliveryId}`, data);
      toast.success('Заявка обновлена!');
      navigate(`/deliveries/${deliveryId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div></div>;
  }

  if (!delivery) return null;

  const isDelivery = formData.delivery_type === 'delivery';

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="edit-delivery-form">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
          Редактирование заявки {delivery.display_number || `#${delivery.id.slice(0, 8)}`}
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Внесите изменения и сохраните</p>
      </div>

      <Card className="border-0 shadow-lg shadow-slate-200/50 dark:shadow-none dark:bg-slate-800">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Тип заявки - только для просмотра */}
            <div className="space-y-2">
              <Label>Тип заявки</Label>
              <div className="flex gap-4">
                <div className={`flex-1 p-3 rounded-lg border-2 ${isDelivery ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20' : 'border-slate-200 dark:border-slate-700 opacity-50'}`}>
                  <div className="flex items-center gap-2">
                    <Truck className="w-5 h-5 text-sky-500" />
                    <span className="font-medium">Доставка</span>
                  </div>
                </div>
                <div className={`flex-1 p-3 rounded-lg border-2 ${!isDelivery ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20' : 'border-slate-200 dark:border-slate-700 opacity-50'}`}>
                  <div className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-sky-500" />
                    <span className="font-medium">Забор груза</span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-slate-500">Тип заявки нельзя изменить</p>
            </div>

            {/* Отправитель */}
            <div className="space-y-2">
              <Label>Отправитель</Label>
              <Input
                placeholder="ФИО или название организации"
                value={formData.sender}
                onChange={(e) => setFormData({ ...formData, sender: e.target.value })}
                data-testid="sender"
              />
            </div>

            {isDelivery ? (
              <>
                {/* Поля для доставки */}
                <div className="space-y-2">
                  <Label>Получатель *</Label>
                  <Input
                    required
                    placeholder="ФИО или название организации"
                    value={formData.recipient_name}
                    onChange={(e) => setFormData({ ...formData, recipient_name: e.target.value })}
                    data-testid="recipient_name"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Адрес доставки *</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <Input
                      required
                      className="pl-10"
                      placeholder="Улица, дом, квартира/офис"
                      value={formData.address_to}
                      onChange={(e) => setFormData({ ...formData, address_to: e.target.value })}
                      data-testid="address_to"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Транспортная компания</Label>
                    <Select
                      value={formData.tk_id}
                      onValueChange={(v) => setFormData({ ...formData, tk_id: v })}
                    >
                      <SelectTrigger data-testid="tk_select">
                        <SelectValue placeholder="Выберите ТК" />
                      </SelectTrigger>
                      <SelectContent>
                        {directories.tk.map((tk) => (
                          <SelectItem key={tk.id} value={tk.id}>{tk.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Количество мест</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.places_count}
                      onChange={(e) => setFormData({ ...formData, places_count: parseInt(e.target.value) || 1 })}
                      data-testid="places_count"
                    />
                  </div>
                </div>

                {/* Сейф-пакет */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_safe_package"
                      checked={formData.is_safe_package}
                      onChange={(e) => setFormData({ ...formData, is_safe_package: e.target.checked })}
                      className="rounded"
                    />
                    <Label htmlFor="is_safe_package" className="cursor-pointer">Сейф-пакет</Label>
                  </div>
                  {formData.is_safe_package && (
                    <Input
                      placeholder="Номер сейф-пакета"
                      value={formData.safe_package_number}
                      onChange={(e) => setFormData({ ...formData, safe_package_number: e.target.value })}
                      data-testid="safe_package_number"
                    />
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Поля для забора груза */}
                <div className="space-y-2">
                  <Label>Получатель *</Label>
                  <Select
                    value={formData.recipient_id}
                    onValueChange={(v) => setFormData({ ...formData, recipient_id: v })}
                  >
                    <SelectTrigger data-testid="recipient_select">
                      <SelectValue placeholder="Выберите получателя" />
                    </SelectTrigger>
                    <SelectContent>
                      {directories.recipients.map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Адрес забора *</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <Input
                      required
                      className="pl-10"
                      placeholder="Улица, дом"
                      value={formData.address_from}
                      onChange={(e) => setFormData({ ...formData, address_from: e.target.value })}
                      data-testid="address_from"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Номер приходного листа</Label>
                  <Input
                    placeholder="Введите номер"
                    value={formData.receipt_number}
                    onChange={(e) => setFormData({ ...formData, receipt_number: e.target.value })}
                    data-testid="receipt_number"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Обработчик</Label>
                  <Select
                    value={formData.handler_id}
                    onValueChange={(v) => setFormData({ ...formData, handler_id: v })}
                  >
                    <SelectTrigger data-testid="handler_select">
                      <SelectValue placeholder="Выберите обработчика" />
                    </SelectTrigger>
                    <SelectContent>
                      {directories.handlers.map((h) => (
                        <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Комментарий</Label>
                  <Textarea
                    placeholder="Дополнительная информация..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    data-testid="notes"
                  />
                </div>
              </>
            )}

            {/* Дедлайн */}
            <div className="space-y-2">
              <Label>Дедлайн</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                <Input
                  type="date"
                  className="pl-10"
                  value={formData.deadline}
                  onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                  data-testid="deadline"
                />
              </div>
            </div>

            {/* Примечания для доставки */}
            {isDelivery && (
              <div className="space-y-2">
                <Label>Примечания</Label>
                <Textarea
                  placeholder="Дополнительная информация..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  data-testid="notes"
                />
              </div>
            )}

            <div className="flex gap-4">
              <Button type="button" variant="outline" onClick={() => navigate(`/deliveries/${deliveryId}`)} className="flex-1">
                Отмена
              </Button>
              <Button type="submit" disabled={saving} className="flex-1 btn-primary" data-testid="save-delivery">
                {saving ? 'Сохранение...' : 'Сохранить'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// ============ DELIVERY DETAIL ============

const DeliveryDetail = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const deliveryId = window.location.pathname.split('/').pop();
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDelivery = async () => {
      try {
        const res = await api.get(`/deliveries/${deliveryId}`);
        setDelivery(res.data);
      } catch (err) {
        toast.error('Заявка не найдена');
        navigate('/deliveries');
      } finally {
        setLoading(false);
      }
    };
    fetchDelivery();
  }, [deliveryId, navigate]);

  const statusColors = {
    new: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    in_transit: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
    delivered: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
  };

  const statusLabels = {
    new: 'Новая',
    in_transit: 'В пути',
    delivered: 'Доставлено'
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div></div>;
  }

  if (!delivery) return null;

  // Check if customer can edit (only if status is 'new' and they are the author)
  const canEdit = user.role === 'customer' && delivery.status === 'new' && delivery.customer_id === user.id;
  // Admins and managers can always edit
  const canEditAdmin = ['admin', 'manager'].includes(user.role);

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="delivery-detail">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">Заявка {delivery.display_number || `#${delivery.id.slice(0, 8)}`}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Создана: {new Date(delivery.created_at).toLocaleString('ru-RU')}
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          {(canEdit || canEditAdmin) && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate(`/deliveries/${delivery.id}/edit`)}
              data-testid="edit-delivery-btn"
            >
              <Pencil className="w-4 h-4 mr-2" />
              Редактировать
            </Button>
          )}
          <Badge className={`${statusColors[delivery.status]} text-sm px-3 py-1`}>
            {statusLabels[delivery.status]}
          </Badge>
        </div>
      </div>

      <Card className="border-0 shadow-lg shadow-slate-200/50 dark:shadow-none dark:bg-slate-800">
        <CardContent className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Тип</p>
              <p className="font-medium text-slate-900 dark:text-white">
                {delivery.delivery_type === 'delivery' ? 'Доставка' : 'Забор груза'}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Заказчик</p>
              <p className="font-medium text-slate-900 dark:text-white">{delivery.customer_name || '-'}</p>
            </div>
          </div>

          <Separator />

          {/* Отправитель и Получатель */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Отправитель</p>
              <p className="font-medium text-slate-900 dark:text-white">{delivery.sender || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Получатель</p>
              <p className="font-medium text-slate-900 dark:text-white">{delivery.recipient_name || '-'}</p>
            </div>
          </div>

          <Separator />

          {/* Адреса */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Адрес отправления</p>
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-slate-400 mt-1 flex-shrink-0" />
                <p className="font-medium text-slate-900 dark:text-white">{delivery.address_from || '-'}</p>
              </div>
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Адрес доставки</p>
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-slate-400 mt-1 flex-shrink-0" />
                <p className="font-medium text-slate-900 dark:text-white">{delivery.address_to || '-'}</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* ТК, Количество мест, Дедлайн */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Транспортная компания</p>
              <p className="font-medium text-slate-900 dark:text-white">{delivery.tk_name || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Количество мест</p>
              <p className="font-medium text-slate-900 dark:text-white">{delivery.places_count || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Дедлайн</p>
              <p className="font-medium text-slate-900 dark:text-white">{delivery.deadline || '-'}</p>
            </div>
            {delivery.is_safe_package && (
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Сейф-пакет</p>
                <p className="font-medium text-slate-900 dark:text-white">{delivery.safe_package_number || 'Да'}</p>
              </div>
            )}
          </div>

          {delivery.description && (
            <>
              <Separator />
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Описание груза</p>
                <p className="text-slate-900 dark:text-white">{delivery.description}</p>
              </div>
            </>
          )}

          {delivery.driver_name && (
            <>
              <Separator />
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Водитель</p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center">
                    <User className="w-4 h-4" />
                  </div>
                  <p className="font-medium text-slate-900 dark:text-white">{delivery.driver_name}</p>
                </div>
              </div>
            </>
          )}

          {delivery.notes && (
            <>
              <Separator />
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Примечания</p>
                <p className="text-slate-900 dark:text-white">{delivery.notes}</p>
              </div>
            </>
          )}

          {delivery.receipt_number && (
            <>
              <Separator />
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Номер приходного листа</p>
                <p className="font-mono text-slate-900 dark:text-white">{delivery.receipt_number}</p>
              </div>
            </>
          )}

          {delivery.handler_name && (
            <>
              <Separator />
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Кому передать</p>
                <p className="font-medium text-slate-900 dark:text-white">{delivery.handler_name}</p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Button variant="outline" onClick={() => navigate('/deliveries')}>
        Назад к списку
      </Button>
    </div>
  );
};

// ============ USERS PAGE ============

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    name: '',
    role: 'customer',
    phone: '',
    telegram_id: '',
    is_active: true
  });

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch (err) {
      toast.error('Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreate = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) {
      toast.error('Заполните обязательные поля');
      return;
    }
    try {
      await api.post('/users', newUser);
      toast.success('Пользователь создан');
      setCreateDialogOpen(false);
      setNewUser({
        email: '',
        password: '',
        name: '',
        role: 'customer',
        phone: '',
        telegram_id: '',
        is_active: true
      });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка создания');
    }
  };

  const handleUpdate = async () => {
    try {
      await api.put(`/users/${editUser.id}`, {
        name: editUser.name,
        role: editUser.role,
        phone: editUser.phone,
        telegram_id: editUser.telegram_id,
        is_active: editUser.is_active
      });
      toast.success('Пользователь обновлен');
      setEditDialogOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error('Ошибка обновления');
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error('Пароль должен содержать минимум 4 символа');
      return;
    }
    try {
      await api.put(`/users/${selectedUserId}/password`, { new_password: newPassword });
      toast.success('Пароль изменен');
      setPasswordDialogOpen(false);
      setNewPassword('');
      setSelectedUserId(null);
    } catch (err) {
      toast.error('Ошибка смены пароля');
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      const res = await api.put(`/users/${userId}/toggle-active`);
      toast.success(res.data.message);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Удалить пользователя? Это действие нельзя отменить.')) return;
    try {
      await api.delete(`/users/${userId}`);
      toast.success('Пользователь удален');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const roleLabels = {
    admin: 'Администратор',
    manager: 'Руководитель',
    driver: 'Водитель',
    customer: 'Заказчик'
  };

  const roleColors = {
    admin: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    manager: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    driver: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
    customer: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
  };

  const isAdmin = currentUser?.role === 'admin';

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Пользователи</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Управление пользователями системы</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-user-btn">
            <Plus className="w-4 h-4 mr-2" />
            Создать пользователя
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div></div>
      ) : (
        <Card className="border-0 shadow-lg shadow-slate-200/50 dark:shadow-none dark:bg-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
          <Table className="min-w-[900px]">
            <TableHeader>
              <TableRow>
                <TableHead>Имя</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Роль</TableHead>
                <TableHead>Телефон</TableHead>
                <TableHead>Telegram</TableHead>
                <TableHead>Bitrix24</TableHead>
                <TableHead>Статус</TableHead>
                {isAdmin && <TableHead className="text-right">Действия</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} className={!user.is_active ? 'opacity-50' : ''}>
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Badge className={roleColors[user.role]}>{roleLabels[user.role]}</Badge>
                  </TableCell>
                  <TableCell>{user.phone || '-'}</TableCell>
                  <TableCell>{user.telegram_id || '-'}</TableCell>
                  <TableCell>{user.bitrix24_id || '-'}</TableCell>
                  <TableCell>
                    {isAdmin && user.id !== currentUser.id ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleActive(user.id)}
                        className={user.is_active ? 'text-emerald-600' : 'text-red-500'}
                        data-testid={`toggle-active-${user.id}`}
                      >
                        {user.is_active ? 'Активен' : 'Заблокирован'}
                      </Button>
                    ) : (
                      <Badge variant={user.is_active ? 'default' : 'secondary'}>
                        {user.is_active ? 'Активен' : 'Заблокирован'}
                      </Badge>
                    )}
                  </TableCell>
                  {isAdmin && (
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`user-menu-${user.id}`}>
                              <Settings className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { setEditUser(user); setEditDialogOpen(true); }}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Редактировать
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setSelectedUserId(user.id); setPasswordDialogOpen(true); }}>
                              <FileText className="w-4 h-4 mr-2" />
                              Сменить пароль
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleToggleActive(user.id)}
                              disabled={user.id === currentUser.id}
                            >
                              {user.is_active ? (
                                <>
                                  <X className="w-4 h-4 mr-2" />
                                  Заблокировать
                                </>
                              ) : (
                                <>
                                  <Check className="w-4 h-4 mr-2" />
                                  Разблокировать
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleDelete(user.id)}
                              disabled={user.id === currentUser.id}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Удалить
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </Card>
      )}

      {/* Create user dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Создание пользователя</DialogTitle>
            <DialogDescription>Заполните данные нового пользователя</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Имя *</Label>
              <Input
                placeholder="Иван Иванов"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                data-testid="new-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                placeholder="email@example.com"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                data-testid="new-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Пароль *</Label>
              <Input
                type="password"
                placeholder="Минимум 4 символа"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                data-testid="new-user-password"
              />
            </div>
            <div className="space-y-2">
              <Label>Роль</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Администратор</SelectItem>
                  <SelectItem value="manager">Руководитель</SelectItem>
                  <SelectItem value="driver">Водитель</SelectItem>
                  <SelectItem value="customer">Заказчик</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Телефон</Label>
                <Input
                  placeholder="+7..."
                  value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Telegram ID</Label>
                <Input
                  placeholder="@username"
                  value={newUser.telegram_id}
                  onChange={(e) => setNewUser({ ...newUser, telegram_id: e.target.value })}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={newUser.is_active}
                onCheckedChange={(v) => setNewUser({ ...newUser, is_active: v })}
              />
              <Label>Активен</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Отмена</Button>
            <Button onClick={handleCreate} data-testid="confirm-create-user">Создать</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Редактирование пользователя</DialogTitle>
            <DialogDescription>{editUser?.email}</DialogDescription>
          </DialogHeader>
          {editUser && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Имя</Label>
                <Input
                  value={editUser.name}
                  onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Роль</Label>
                <Select value={editUser.role} onValueChange={(v) => setEditUser({ ...editUser, role: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Администратор</SelectItem>
                    <SelectItem value="manager">Руководитель</SelectItem>
                    <SelectItem value="driver">Водитель</SelectItem>
                    <SelectItem value="customer">Заказчик</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Телефон</Label>
                  <Input
                    value={editUser.phone || ''}
                    onChange={(e) => setEditUser({ ...editUser, phone: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telegram ID</Label>
                  <Input
                    value={editUser.telegram_id || ''}
                    onChange={(e) => setEditUser({ ...editUser, telegram_id: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Bitrix24 ID</Label>
                <Input
                  value={editUser.bitrix24_id || ''}
                  onChange={(e) => setEditUser({ ...editUser, bitrix24_id: e.target.value })}
                  placeholder="ID пользователя в Битрикс24"
                />
                <p className="text-xs text-slate-500">Числовой ID пользователя для уведомлений через Bitrix24</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={editUser.is_active}
                  onCheckedChange={(v) => setEditUser({ ...editUser, is_active: v })}
                  disabled={editUser.id === currentUser?.id}
                />
                <Label>Активен</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Отмена</Button>
            <Button onClick={handleUpdate}>Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change password dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Смена пароля</DialogTitle>
            <DialogDescription>Введите новый пароль для пользователя</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Новый пароль</Label>
              <Input
                type="password"
                placeholder="Минимум 4 символа"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="new-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPasswordDialogOpen(false); setNewPassword(''); }}>Отмена</Button>
            <Button onClick={handleChangePassword} data-testid="confirm-change-password">Сменить пароль</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ DRIVERS PAGE ============

const DriversPage = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const res = await api.get('/drivers');
        setDrivers(res.data);
      } catch (err) {
        toast.error('Ошибка загрузки');
      } finally {
        setLoading(false);
      }
    };
    fetchDrivers();
  }, []);

  return (
    <div className="space-y-6" data-testid="drivers-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Водители</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Список активных водителей</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div></div>
      ) : drivers.length === 0 ? (
        <Card className="border-0 shadow-lg dark:bg-slate-800">
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <p className="text-slate-500 dark:text-slate-400">Водители не найдены</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {drivers.map((driver) => (
            <Card key={driver.id} className="border-0 shadow-lg shadow-slate-200/50 dark:shadow-none dark:bg-slate-800">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center">
                    <User className="w-6 h-6 text-sky-600 dark:text-sky-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white">{driver.name}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{driver.email}</p>
                    {driver.phone && (
                      <p className="text-sm text-slate-500 dark:text-slate-400">{driver.phone}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ SETTINGS PAGE ============

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('directories');
  const [settings, setSettings] = useState({
    telegram_bot_token: '',
    telegram_enabled: false,
    bitrix24_webhook_url: '',
    bitrix24_enabled: false,
    sso_endpoint: '',
    sso_enabled: false,
    notification_templates: {
      delivery_created: '🆕 Заявка {display_number} создана',
      driver_assigned: '🚚 Вам назначена заявка {display_number}',
      status_changed: '📦 Статус заявки {display_number} изменен: {status}',
      mass_assigned: '🚚 Вам назначено {count} заявок'
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Directories state
  const [directories, setDirectories] = useState([]);
  const [dirLoading, setDirLoading] = useState(true);
  const [newItemDialog, setNewItemDialog] = useState(false);
  const [newItem, setNewItem] = useState({ name: '', directory_type: 'tk' });
  const [editItem, setEditItem] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/settings/integrations');
        setSettings(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const fetchDirectories = useCallback(async () => {
    try {
      const res = await api.get('/directories');
      setDirectories(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setDirLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDirectories();
  }, [fetchDirectories]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/integrations', settings);
      toast.success('Настройки сохранены');
    } catch (err) {
      toast.error('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateItem = async () => {
    if (!newItem.name.trim()) {
      toast.error('Введите название');
      return;
    }
    try {
      await api.post('/directories', newItem);
      toast.success('Элемент добавлен');
      setNewItemDialog(false);
      setNewItem({ name: '', directory_type: 'tk' });
      fetchDirectories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка создания');
    }
  };

  const handleUpdateItem = async () => {
    try {
      await api.put(`/directories/${editItem.id}?name=${encodeURIComponent(editItem.name)}&is_active=${editItem.is_active}`);
      toast.success('Элемент обновлен');
      setEditDialogOpen(false);
      setEditItem(null);
      fetchDirectories();
    } catch (err) {
      toast.error('Ошибка обновления');
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm('Удалить элемент?')) return;
    try {
      await api.delete(`/directories/${id}`);
      toast.success('Элемент удален');
      fetchDirectories();
    } catch (err) {
      toast.error('Ошибка удаления');
    }
  };

  const directoryTypeLabels = {
    tk: 'Транспортные компании (ТК)',
    recipients: 'Получатели (для забора)',
    handlers: 'Кому передать'
  };

  const groupedDirectories = {
    tk: directories.filter(d => d.directory_type === 'tk'),
    recipients: directories.filter(d => d.directory_type === 'recipients'),
    handlers: directories.filter(d => d.directory_type === 'handlers')
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Настройки</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Конфигурация системы</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full sm:w-auto grid grid-cols-3 sm:inline-flex">
          <TabsTrigger value="directories" className="text-xs sm:text-sm">Справочники</TabsTrigger>
          <TabsTrigger value="notifications" className="text-xs sm:text-sm">Уведомления</TabsTrigger>
          <TabsTrigger value="integrations" className="text-xs sm:text-sm">Интеграции</TabsTrigger>
        </TabsList>

        {/* СПРАВОЧНИКИ */}
        <TabsContent value="directories" className="space-y-6">
          <div className="flex justify-end">
            <Button onClick={() => setNewItemDialog(true)} className="btn-primary" data-testid="add-directory-item">
              <Plus className="w-4 h-4 mr-2" />
              Добавить элемент
            </Button>
          </div>

          {dirLoading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div></div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedDirectories).map(([type, items]) => (
                <Card key={type} className="border-0 shadow-lg shadow-slate-200/50 dark:shadow-none dark:bg-slate-800">
                  <CardHeader>
                    <CardTitle className="text-base">{directoryTypeLabels[type]}</CardTitle>
                    <CardDescription>{items.length} элементов</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {items.length === 0 ? (
                      <p className="text-slate-500 dark:text-slate-400 text-sm">Нет элементов</p>
                    ) : (
                      <div className="space-y-2">
                        {items.map((item) => (
                          <div key={item.id} className={`flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50 ${!item.is_active ? 'opacity-50' : ''}`}>
                            <span className="font-medium text-slate-900 dark:text-white">{item.name}</span>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" onClick={() => { setEditItem(item); setEditDialogOpen(true); }}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteItem(item.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Add item dialog */}
          <Dialog open={newItemDialog} onOpenChange={setNewItemDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Добавить элемент справочника</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Тип справочника</Label>
                  <Select value={newItem.directory_type} onValueChange={(v) => setNewItem({ ...newItem, directory_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tk">Транспортные компании (ТК)</SelectItem>
                      <SelectItem value="recipients">Получатели (для забора)</SelectItem>
                      <SelectItem value="handlers">Кому передать</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Название</Label>
                  <Input
                    placeholder="Введите название"
                    value={newItem.name}
                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                    data-testid="new-directory-name"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setNewItemDialog(false)}>Отмена</Button>
                <Button onClick={handleCreateItem}>Добавить</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Edit item dialog */}
          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Редактировать элемент</DialogTitle>
              </DialogHeader>
              {editItem && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Название</Label>
                    <Input
                      value={editItem.name}
                      onChange={(e) => setEditItem({ ...editItem, name: e.target.value })}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={editItem.is_active}
                      onCheckedChange={(v) => setEditItem({ ...editItem, is_active: v })}
                    />
                    <Label>Активен</Label>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Отмена</Button>
                <Button onClick={handleUpdateItem}>Сохранить</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* УВЕДОМЛЕНИЯ */}
        <TabsContent value="notifications" className="space-y-6">
          {/* Справка по переменным */}
          <Card className="border-0 shadow-lg shadow-slate-200/50 dark:shadow-none dark:bg-slate-800 bg-sky-50 dark:bg-sky-900/20">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-5 h-5 text-sky-500" />
                Доступные переменные для шаблонов
              </CardTitle>
              <CardDescription>
                Используйте эти переменные в фигурных скобках для персонализации уведомлений
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Заявка:</p>
                  <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1">
                    <li><code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">{'{display_number}'}</code> — номер</li>
                    <li><code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">{'{status}'}</code> — статус</li>
                    <li><code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">{'{delivery_type}'}</code> — тип</li>
                    <li><code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">{'{delivery_link}'}</code> — ссылка</li>
                    <li><code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">{'{deadline}'}</code> — дедлайн</li>
                    <li><code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">{'{notes}'}</code> — примечания</li>
                  </ul>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Участники:</p>
                  <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1">
                    <li><code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">{'{sender}'}</code> — отправитель</li>
                    <li><code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">{'{recipient_name}'}</code> — получатель</li>
                    <li><code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">{'{customer_name}'}</code> — заказчик</li>
                    <li><code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">{'{driver_name}'}</code> — водитель</li>
                    <li><code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">{'{handler_name}'}</code> — обработчик</li>
                  </ul>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Детали:</p>
                  <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1">
                    <li><code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">{'{address_to}'}</code> — адрес доставки</li>
                    <li><code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">{'{address_from}'}</code> — адрес забора</li>
                    <li><code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">{'{places_count}'}</code> — кол-во мест</li>
                    <li><code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">{'{tk_name}'}</code> — ТК</li>
                    <li><code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">{'{safe_package_number}'}</code> — сейф-пакет</li>
                    <li><code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">{'{receipt_number}'}</code> — № приходного</li>
                    <li><code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">{'{count}'}</code> — кол-во (массовое)</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg shadow-slate-200/50 dark:shadow-none dark:bg-slate-800">
            <CardHeader>
              <CardTitle>Шаблоны уведомлений Telegram</CardTitle>
              <CardDescription>
                Настройте текст уведомлений. Используйте переменные в фигурных скобках.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Создание заявки</Label>
                <Input
                  value={settings.notification_templates?.delivery_created || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    notification_templates: { ...settings.notification_templates, delivery_created: e.target.value }
                  })}
                  placeholder="🆕 Заявка {display_number} создана"
                />
                <p className="text-xs text-slate-500">Переменные: {'{display_number}'}, {'{delivery_link}'}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Назначение водителя</Label>
                <Input
                  value={settings.notification_templates?.driver_assigned || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    notification_templates: { ...settings.notification_templates, driver_assigned: e.target.value }
                  })}
                  placeholder="🚚 Вам назначена заявка {display_number}. Ссылка: {delivery_link}"
                />
                <p className="text-xs text-slate-500">Переменные: {'{display_number}'}, {'{delivery_link}'}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Изменение статуса</Label>
                <Input
                  value={settings.notification_templates?.status_changed || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    notification_templates: { ...settings.notification_templates, status_changed: e.target.value }
                  })}
                  placeholder="📦 Статус заявки {display_number} изменен: {status}"
                />
                <p className="text-xs text-slate-500">Переменные: {'{display_number}'}, {'{status}'}, {'{delivery_link}'}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Массовое назначение</Label>
                <Input
                  value={settings.notification_templates?.mass_assigned || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    notification_templates: { ...settings.notification_templates, mass_assigned: e.target.value }
                  })}
                  placeholder="🚚 Вам назначено {count} заявок"
                />
                <p className="text-xs text-slate-500">Переменные: {'{count}'}</p>
              </div>
            </CardContent>
          </Card>
          
          <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-notifications">
            {saving ? 'Сохранение...' : 'Сохранить шаблоны'}
          </Button>
        </TabsContent>

        {/* ИНТЕГРАЦИИ */}
        <TabsContent value="integrations" className="space-y-6">
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div></div>
          ) : (
            <>
              {/* Telegram */}
              <Card className="border-0 shadow-lg shadow-slate-200/50 dark:shadow-none dark:bg-slate-800">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center">
                        <Send className="w-5 h-5 text-sky-600 dark:text-sky-400" />
                      </div>
                      <div>
                        <CardTitle className="text-base">Telegram Bot</CardTitle>
                        <CardDescription>Уведомления через Telegram</CardDescription>
                      </div>
                    </div>
                    <Switch
                      checked={settings.telegram_enabled}
                      onCheckedChange={(v) => setSettings({ ...settings, telegram_enabled: v })}
                      data-testid="telegram-toggle"
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Label>Bot Token</Label>
                    <Input
                      type="password"
                      placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
                      value={settings.telegram_bot_token || ''}
                      onChange={(e) => setSettings({ ...settings, telegram_bot_token: e.target.value })}
                      data-testid="telegram-token"
                    />
                    <p className="text-xs text-slate-500">Получите токен у @BotFather в Telegram</p>
                  </div>
                </CardContent>
              </Card>

              {/* Bitrix24 */}
              <Card className="border-0 shadow-lg shadow-slate-200/50 dark:shadow-none dark:bg-slate-800">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                        <Bell className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div>
                        <CardTitle className="text-base">Bitrix24</CardTitle>
                        <CardDescription>Интеграция с Bitrix24</CardDescription>
                      </div>
                    </div>
                    <Switch
                      checked={settings.bitrix24_enabled}
                      onCheckedChange={(v) => setSettings({ ...settings, bitrix24_enabled: v })}
                      data-testid="bitrix-toggle"
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Label>Webhook URL</Label>
                    <Input
                      placeholder="https://your-domain.bitrix24.ru/rest/..."
                      value={settings.bitrix24_webhook_url || ''}
                      onChange={(e) => setSettings({ ...settings, bitrix24_webhook_url: e.target.value })}
                      data-testid="bitrix-url"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* SSO */}
              <Card className="border-0 shadow-lg shadow-slate-200/50 dark:shadow-none dark:bg-slate-800">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                        <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <CardTitle className="text-base">SSO (IRISK ID)</CardTitle>
                        <CardDescription>Единый вход через внешнюю систему</CardDescription>
                      </div>
                    </div>
                    <Switch
                      checked={settings.sso_enabled}
                      onCheckedChange={(v) => setSettings({ ...settings, sso_enabled: v })}
                      data-testid="sso-toggle"
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Label>SSO Endpoint</Label>
                    <Input
                      placeholder="https://ssp.irisk-group.com/api/auth/sso"
                      value={settings.sso_endpoint || ''}
                      onChange={(e) => setSettings({ ...settings, sso_endpoint: e.target.value })}
                      data-testid="sso-endpoint"
                    />
                    <p className="text-xs text-slate-500">SSO интеграция настроена через IRISK ID</p>
                  </div>
                </CardContent>
              </Card>

              <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-settings">
                {saving ? 'Сохранение...' : 'Сохранить настройки'}
              </Button>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ============ APP ============

// Smart redirect component
const SmartRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div></div>;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  // Admin goes to dashboard, others go to deliveries
  if (user.role === 'admin') {
    return <Navigate to="/dashboard" />;
  }
  return <Navigate to="/deliveries" />;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/sso-callback" element={<SSOCallback />} />
            <Route path="/" element={<SmartRedirect />} />
            <Route path="/dashboard" element={<ProtectedRoute roles={['admin']}><Layout><Dashboard /></Layout></ProtectedRoute>} />
            <Route path="/deliveries" element={<ProtectedRoute><Layout><DeliveriesList /></Layout></ProtectedRoute>} />
            <Route path="/deliveries/new" element={<ProtectedRoute roles={['customer', 'admin', 'manager']}><Layout><NewDelivery /></Layout></ProtectedRoute>} />
            <Route path="/deliveries/:deliveryId/edit" element={<ProtectedRoute roles={['customer', 'admin', 'manager']}><Layout><EditDelivery /></Layout></ProtectedRoute>} />
            <Route path="/deliveries/:id" element={<ProtectedRoute><Layout><DeliveryDetail /></Layout></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute roles={['admin']}><Layout><UsersPage /></Layout></ProtectedRoute>} />
            <Route path="/drivers" element={<ProtectedRoute roles={['admin', 'manager']}><Layout><DriversPage /></Layout></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute roles={['admin', 'manager']}><Layout><SettingsPage /></Layout></ProtectedRoute>} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
